import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Sport } from '__generated__/globalTypes';
import { Button } from 'atoms/buttons/Button';
import { SBHorizontal } from 'atoms/layout/flex';
import { DotsLoader } from 'atoms/loader/DotsLoader';
import { BodyM } from 'atoms/typography';
import { Bold } from 'atoms/typography/Bold';
import { TermsLink } from 'components/conversionCredit/DumbConversionCreditBanner/ui';
import { useCurrentUserContext } from 'contexts/currentUser';
import { useEventsContext } from 'contexts/events';
import { useIntlContext } from 'contexts/intl';
import useScreenSize from 'hooks/device/useScreenSize';
import { useInviteLink } from 'hooks/useInviteLink';
import useLifecycle, { LIFECYCLE } from 'hooks/useLifecycle';
import { useReferralProgramConfig } from 'hooks/useReferralProgramConfig';
import { glossary } from 'lib/glossary';
import { tabletAndAbove } from 'style/mediaQuery';

import { TermsDialog } from '../../conversionCredit/TermsDialog';
import { DiscountBannerWrapper } from '../DiscountBannerWrapper';

const Buttons = styled(SBHorizontal)`
  width: 100%;
  @media ${tabletAndAbove} {
    width: auto;
  }
`;

type Props = {
  sport: Sport;
};
export const ReferralBanner = ({ sport }: Props) => {
  const { userFiatAmount: referralProgramConfigFiatAmount } =
    useReferralProgramConfig(sport);
  const { currentUser, fiatCurrency } = useCurrentUserContext();
  const { up: isTabletOrDesktop } = useScreenSize('tablet');
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const { formatNumber } = useIntlContext();
  const { track } = useEventsContext();
  const inviteLink = useInviteLink(sport);
  const { update, lifecycle } = useLifecycle();

  const hideReferralBanner = lifecycle?.hideReferralBanner;

  if (!currentUser || hideReferralBanner) return null;

  const { referralRewardsCount } = currentUser;

  if (referralRewardsCount > 0) return null;

  return (
    <DiscountBannerWrapper
      rightNode={
        <Buttons gap={1.5}>
          <Button
            size="small"
            color="transparent"
            fullWidth={!isTabletOrDesktop}
            onClick={() => {
              update(LIFECYCLE.hideReferralBanner, true);
            }}
          >
            <FormattedMessage
              id="ReferralBanner.dismiss"
              defaultMessage="Dismiss"
            />
          </Button>
          <Button
            size="small"
            color="secondary"
            fullWidth={!isTabletOrDesktop}
            to={inviteLink}
            onClick={() => track('Click Referral Banner')}
          >
            <FormattedMessage
              id="ReferralBanner.referAFriend"
              defaultMessage="Refer a friend"
            />
          </Button>
        </Buttons>
      }
    >
      {referralProgramConfigFiatAmount === undefined ? (
        <DotsLoader />
      ) : (
        <BodyM as="span" color="var(--c-static-neutral-100)">
          <FormattedMessage
            id="ReferralBanner.getDiscountReward"
            defaultMessage="Get <b>{amount}</b> to use on the market when referring a friend."
            values={{
              b: Bold,
              amount: formatNumber(referralProgramConfigFiatAmount, {
                style: 'currency',
                currency: fiatCurrency.code,
                maximumFractionDigits: 0,
              }),
            }}
          />{' '}
          <TermsLink type="button" onClick={() => setOpenDialog(true)}>
            <FormattedMessage {...glossary.termsApply} />
          </TermsLink>
        </BodyM>
      )}
      {openDialog && <TermsDialog onClose={() => setOpenDialog(false)} />}
    </DiscountBannerWrapper>
  );
};
